import axios from 'axios'
import { router, usePage } from '@inertiajs/vue3'
import { computed, ref, shallowRef } from 'vue'
import { useEvaluationStore } from '@/stores/useEvaluationStore'

/**
 * RED-317 — the evaluator's Turuülevaade choice beside a universal mall.
 *
 * The truth lives in the `marketOverviewChoice` Inertia prop (null while the
 * `market-overviews` flag is off): the company's published market cells, the
 * automatic match with its grounds, and the stored choice. After every save the
 * composable partially reloads that prop instead of trusting the JSON echo, so
 * what the selector shows is what a page refresh shows (the RED-235 lesson).
 */
export function useMarketOverviewChoice() {
  const page = usePage()
  const evaluationStore = useEvaluationStore()

  const choice = computed(() => page.props.marketOverviewChoice ?? null)
  const saving = shallowRef(false)
  /** @type {import('vue').Ref<'unavailable' | 'generic' | null>} */
  const errorCode = ref(null)

  const refresh = () =>
    new Promise(resolve => {
      router.reload({ only: ['marketOverviewChoice'], onFinish: resolve })
    })

  /**
   * Store a cell as the choice, or `null` to return to the automatic match.
   * Resolves true once the choice landed and the prop was re-read.
   *
   * @param {{ county: string|null, purpose: string } | null} cell
   */
  const save = async cell => {
    errorCode.value = null
    saving.value = true
    try {
      // A pending field autosave rewrites the whole assessment_data server-side;
      // let it land first so it cannot overwrite this choice.
      await evaluationStore.flushSave()
      await axios.put(route('evaluation.market-overview.update', page.props.evaluationId), {
        county: cell?.county ?? null,
        purpose: cell?.purpose ?? null,
      })
      await refresh()
      return true
    } catch (err) {
      errorCode.value = err?.response?.status === 422 ? 'unavailable' : 'generic'
      return false
    } finally {
      saving.value = false
    }
  }

  return { choice, saving, errorCode, save }
}
